// One tense, after the reference design (prakrit.netlify.app/grammar/vartamana): what the tense is for, the endings by
// person and number on two tabs (१. सामान्य · २. विकल्प), root + ending cards, examples, three quick questions and every
// form of the chosen root on the same two tabs. ?root=पढ opens another root. Every form comes from the grammar engine.

import type { FC, ReactNode } from 'react'
import type { Lang, Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import {
    ArrowRight,
    ChevronRight,
    FlaskConical,
    Grid2x2,
    Info
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { pratyayaTable, sameInEveryCell } from '@/prakrit/lib/grammar/pratyaya'
import { TENSE_LABEL, TENSE_SHORT } from '@/prakrit/lib/grammar/labels'
import { AS_DHATU } from '@/prakrit/lib/grammar/verbs'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { Bi, Eyebrow } from '@/prakrit/components/common/bi'
import { DhatuPicture, hasDhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { TriLine } from '@/prakrit/components/common/tri-line'
import { useUrlQuery } from '@/prakrit/components/grammar/labs/use-url-query'
import { LabNav, Notice, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { RootCombobox } from '@/prakrit/components/grammar/dhatu/root-combobox'
import { ChipButton, PANEL, pillKeyNav } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { cellGloss, sentenceMeaning, subjectOf } from '@/prakrit/components/grammar/dhatu/meaning'
import { practiceHref } from '@/prakrit/components/practice/practice-topics'
import { KalTabs } from '@/prakrit/components/grammar/kal/kal-tabs'
import { PratyayaTableView } from '@/prakrit/components/grammar/kal/pratyaya-table'
import { FormationCards } from '@/prakrit/components/grammar/kal/formation-cards'
import { FormsGrid } from '@/prakrit/components/grammar/kal/forms-grid'
import { PratyayaQuiz } from '@/prakrit/components/grammar/kal/pratyaya-quiz'

import {
    DEFAULT_KAL_ROOT,
    LONG_EXAMPLE_ROOT,
    TENSE_GROUPS,
    TENSE_INFO,
    TENSE_NOTES,
    kalHref,
    rootsForTense
} from '@/prakrit/components/grammar/kal/kal-data'

const WIDTH = 'mx-auto w-full max-w-6xl px-4 sm:px-6'

/** Roots offered as one-tap pills (when this tense has them); every other root is in the list. */
const QUICK_ROOTS = ['हस', 'पढ', 'गच्छ', 'पास', 'दा', 'हो']

const PRACTICE_TOPIC: Partial<Record<Tense, string>> = {
    vartaman: 'vartaman',
    vidhi: 'vidhi',
    bhoot: 'bhoot',
    bhavishya: 'bhavishya',
    karmani_vartaman: 'karmani_vartaman',
    prerak_vartaman: 'prerak_vartaman',
    kriyatipatti: 'kriyatipatti'
}

const practiceLink = (tense: Tense): string => {
    const level = tense === 'vartaman' ? 'arambh' : tense === 'vidhi' || tense === 'bhoot' ? 'abhyas' : 'praveen'
    const topic = PRACTICE_TOPIC[tense]

    return practiceHref('dhatu-chunauti', level, topic ? [topic] : [])
}

const Section: FC<{ id: string, hi: string, en: string, children: ReactNode, intro?: Lang }> = ({ id, hi, en, children, intro }): ReactNode => (
    <section id={id} aria-labelledby={`${id}-title`} className='flex scroll-mt-24 flex-col gap-5'>
        <div className='flex flex-col gap-1.5'>
            <h2 id={`${id}-title`} className='flex flex-wrap items-baseline gap-x-3 gap-y-0.5'>
                <span lang='hi' className='text-2xl font-bold text-foreground sm:text-[1.75rem]'>{hi}</span>
                <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{en}</span>
            </h2>
            {intro && <Bi hi={intro.hi} en={intro.en} className='max-w-3xl gap-0.5 text-[0.9375rem] leading-relaxed text-muted-foreground' />}
        </div>
        {children}
    </section>
)

const TenseNav: FC<{ current: Tense, root: string }> = ({ current, root }): ReactNode => (
    <nav aria-label='काल चुनें · Choose a tense' className='flex flex-col gap-2.5'>
        {TENSE_GROUPS.map(g => (
            <div key={g.id} className='flex flex-wrap items-center gap-2'>
                <span className='w-full text-xs font-semibold text-muted-foreground sm:w-28'>
                    <span lang='hi' className='hindi'>{g.title.hi}</span>
                </span>
                {g.tenses.map(t => {
                    const active = t === current

                    return (
                        <Link
                            key={t}
                            href={kalHref(t, root)}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                'inline-flex min-h-tap items-center rounded-full border px-3.5 text-sm transition-colors',
                                active
                                    ? 'border-saffron bg-saffron font-semibold text-primary-foreground shadow-sm'
                                    : 'border-border bg-card text-foreground hover:border-saffron/60 hover:bg-saffron/8'
                            )}
                        >
                            <span lang='hi' className='hindi'>{g.basic ? TENSE_LABEL[t].hi : TENSE_SHORT[t].hi}</span>
                        </Link>
                    )
                })}
            </div>
        ))}
    </nav>
)

/** अस (होना) is irregular: its forms are shown as they are, next to the present and past pages. */
const AsNote: FC<{ tense: 'vartaman' | 'bhoot' }> = ({ tense }): ReactNode => {
    const grid = AS_DHATU[tense]
    const forms = tense === 'bhoot'
        ? grid.uttam.eka.map(f => f.form)
        : PERSONS.flatMap(p => NUMBERS.map(n => grid[p][n][0]?.form ?? '')).filter(Boolean)

    return (
        <Notice tone='link' icon={<Info className='size-4 text-terracotta' />}>
            <p className='font-semibold'>
                <span lang='hi' className='hindi'>अस धातु (होना) के रूप अनियमित हैं</span>
                <span aria-hidden='true' className='text-muted-foreground'> · </span>
                <span lang='en' className='font-medium text-muted-foreground'>अस (to be) is irregular</span>
            </p>
            <p lang='pra' className='font-prakrit text-lg font-semibold text-prakrit'>{forms.join(' · ')}</p>
            <Link href={`/grammar/kriya?root=${encodeURIComponent('अस')}&tense=${tense}`} className='inline-flex min-h-tap items-center gap-1.5 self-start text-sm font-semibold text-saffron-strong hover:underline'>
                <span lang='hi' className='hindi'>अस की पूरी तालिका</span>
                <span aria-hidden='true'>·</span>
                <span lang='en'>The full table of अस</span>
                <ArrowRight className='size-4' aria-hidden='true' />
            </Link>
        </Notice>
    )
}

interface KalPageProps {
    tense: Tense
}

export const KalPage: FC<KalPageProps> = ({ tense }): ReactNode => {
    const url = useUrlQuery()
    const roots = useMemo(() => rootsForTense(tense), [tense])
    const fallback = roots.find(r => r.root === DEFAULT_KAL_ROOT)?.root ?? roots[0]?.root ?? DEFAULT_KAL_ROOT
    const [rootId, setRootId] = useState(fallback)
    const [tableMode, setTableMode] = useState<KalMode>('samanya')
    const [formsMode, setFormsMode] = useState<KalMode>('samanya')
    const urlRoot = url.read('root')

    useEffect(() => {
        if (!url.ready) return

        const wanted = urlRoot && roots.some(r => r.root === urlRoot) ? urlRoot : fallback

        setRootId(wanted)
    }, [url.ready, urlRoot, roots, fallback])

    const root = roots.find(r => r.root === rootId) ?? roots[0]
    const table = useMemo(() => (root ? pratyayaTable(tense, root.root, root.rootClass) : null), [tense, root])
    const label = TENSE_LABEL[tense]
    const info = TENSE_INFO[tense]
    const notes = TENSE_NOTES[tense]

    const chooseRoot = (next: string) => {
        setRootId(next)
        url.write({ root: next === DEFAULT_KAL_ROOT ? null : next })
    }

    const quick = QUICK_ROOTS.filter(q => roots.some(r => r.root === q))
    const longRoot = roots.find(r => r.root === LONG_EXAMPLE_ROOT)
    const showLongHint = tense === 'bhoot' && root?.rootClass === 'a' && Boolean(longRoot)

    const examples = useMemo(() => {
        if (!root || !table) return []

        const cells: [Person, Num][] = sameInEveryCell(table)
            ? [['uttam', 'eka'], ['madhyam', 'bahu'], ['anya', 'eka']]
            : [['uttam', 'eka'], ['uttam', 'bahu'], ['anya', 'eka']]

        return cells.flatMap(([p, n]) => {
            const form = table[p][n].samanya[0]?.form

            if (!form) return []

            const meaning = sentenceMeaning(root, tense, p, n) ?? cellGloss(root, tense, p, n)

            return [{ key: `${p}.${n}`, pra: `${subjectOf(p, n)} ${form}।`, ...meaning }]
        })
    }, [root, table, tense])

    if (!root || !table) {
        return (
            <div className={cn(WIDTH, 'py-16')}>
                <Notice tone='danger'>
                    <span lang='hi' className='hindi'>इस काल की तालिका अभी नहीं बन सकी।</span>
                    <span lang='en'>The table for this tense could not be built.</span>
                </Notice>
            </div>
        )
    }

    return (
        <div className='pb-16'>
            <header className={cn(WIDTH, 'flex flex-col gap-3 pb-8 pt-6 sm:pb-10 sm:pt-10')}>
                <nav aria-label='Breadcrumb'>
                    <ol className='flex flex-wrap items-center gap-1 text-sm text-muted-foreground'>
                        <li>
                            <Link href='/grammar' className='inline-flex min-h-tap items-center rounded-full px-1 hover:text-saffron-strong'>
                                <span lang='hi' className='hindi'>व्याकरण</span>
                            </Link>
                        </li>
                        <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                        <li>
                            <Link href='/grammar/kal' className='inline-flex min-h-tap items-center rounded-full px-1 hover:text-saffron-strong'>
                                <span lang='hi' className='hindi'>काल</span>
                            </Link>
                        </li>
                        <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                        <li aria-current='page' className='hindi text-foreground'>{label.hi}</li>
                    </ol>
                </nav>
                <div className='flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex min-w-0 flex-col gap-3'>
                        <Eyebrow>कालः</Eyebrow>
                        <h1 className='flex flex-col gap-1'>
                            <span lang='hi' className='text-[2.125rem] font-bold leading-[1.15] text-foreground sm:text-[2.75rem]'>{label.hi}</span>
                            <span lang='en' className='font-sans text-lg font-medium capitalize text-muted-foreground sm:text-xl'>{label.en}</span>
                        </h1>
                        <Bi hi={info.use.hi} en={info.use.en} className='adults-only mt-1 max-w-3xl gap-1 text-base leading-relaxed text-muted-foreground sm:text-lg' />
                        <Bi hi={info.kids.hi} en={info.kids.en} className='kids-only mt-1 max-w-3xl gap-1 text-base leading-relaxed text-muted-foreground sm:text-lg' />
                        <p className='text-sm text-muted-foreground'>
                            <span lang='hi' data-ex='hi' className='hindi'>नीचे के रूप <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{root.root}</span> ({root.hi}) धातु के हैं; धातु बदलकर देखें।</span>
                            <span aria-hidden='true' className='ex-sep'> · </span>
                            <span lang='en' data-ex='en'>The forms below are of the root {root.root} ({root.en}); change the root to see others.</span>
                        </p>
                    </div>
                    {hasDhatuPicture(root.root) && (
                        <figure key={root.root} className='flex w-40 shrink-0 flex-col items-center gap-2 self-center motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-500 sm:w-48 sm:self-auto lg:w-56'>
                            <DhatuPicture
                                root={root.root}
                                meaning={`${root.hi} · ${root.en}`}
                                sizes='(min-width: 1024px) 224px, 192px'
                                priority
                                className='w-full rotate-1 rounded-3xl shadow-card ring-4 ring-card'
                            />
                            <figcaption className='flex flex-wrap items-baseline justify-center gap-x-1.5 text-center text-sm'>
                                <span lang='pra' className='font-prakrit text-lg font-bold text-prakrit'>{root.root}</span>
                                <span lang='hi' className='hindi text-muted-foreground'>{root.hi}</span>
                                <span lang='en' data-ex='en' className='text-muted-foreground'>· {root.en}</span>
                            </figcaption>
                        </figure>
                    )}
                </div>
                <hr className='mt-4 border-0 border-t border-saffron/45' />
            </header>

            <div className={cn(WIDTH, 'flex flex-col gap-14')}>
                <div className={cn(PANEL, 'flex flex-col gap-5 p-4 sm:p-6')}>
                    <TenseNav current={tense} root={root.root} />
                    <div className='flex flex-col gap-2 border-t border-border pt-4'>
                        <p className='text-xs font-semibold text-muted-foreground'>
                            <span lang='hi' className='hindi'>धातु</span>
                            <span aria-hidden='true'> · </span>
                            <span lang='en' className='font-medium'>root</span>
                        </p>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                            {quick.length > 0 && (
                                <div role='group' aria-label='धातु · Root' onKeyDown={pillKeyNav} className='flex flex-wrap gap-2'>
                                    {quick.map(q => {
                                        const r = roots.find(x => x.root === q)

                                        return (
                                            <ChipButton key={q} pressed={rootId === q} onClick={() => chooseRoot(q)} title={r ? `${r.hi} · ${r.en}` : undefined} className='pl-1.5'>
                                                <DhatuPicture root={q} decorative sizes='32px' className='size-8 rounded-full ring-0' />
                                                <span lang='pra' className='font-prakrit text-base font-semibold'>{q}</span>
                                                {r && <span lang='hi' className='hindi text-xs opacity-80'>{r.hi}</span>}
                                            </ChipButton>
                                        )
                                    })}
                                </div>
                            )}
                            <RootCombobox roots={roots} value={rootId} onChange={chooseRoot} showValue={!quick.includes(rootId)} className='sm:max-w-xs' />
                        </div>
                    </div>
                </div>

                <Section
                    id='pratyaya'
                    hi='पुरुष अनुसार प्रत्यय'
                    en='Endings by person and number'
                    intro={{
                        hi: 'हर पुरुष और वचन का अपना प्रत्यय है। कोष्ठक में वे सर्वनाम हैं जिनके साथ वह प्रत्यय आता है।',
                        en: 'Each person and number has its own ending. In brackets: the pronouns that go with it.'
                    }}
                >
                    <KalTabs id='pratyaya-tabs' value={tableMode} onChange={setTableMode} label={`${label.hi}: प्रत्यय · endings`}>
                        <div className='flex flex-col gap-4'>
                            <PratyayaTableView root={root.root} table={table} mode={tableMode} tenseHi={label.hi} tenseEn={label.en} />
                            {tableMode === 'vikalp' && showLongHint && longRoot && (
                                <Notice tone='info' icon={<Info className='size-4 text-muted-foreground' />}>
                                    <p>
                                        <span lang='hi' data-ex='hi' className='hindi'>आ / ए / ओ से अन्त होने वाली धातुओं के भूत काल में -ही के साथ -सी और -हीअ विकल्प हैं।</span>
                                        <span aria-hidden='true' className='ex-sep'> · </span>
                                        <span lang='en' data-ex='en'>Roots ending in आ, ए or ओ have -सी and -हीअ as variants of -ही in the past.</span>
                                    </p>
                                    <button
                                        type='button'
                                        onClick={() => chooseRoot(longRoot.root)}
                                        className='inline-flex min-h-tap items-center gap-1.5 self-start text-sm font-semibold text-saffron-strong hover:underline'
                                    >
                                        <span lang='hi' className='hindi'><span lang='pra' className='font-prakrit'>{longRoot.root}</span> ({longRoot.hi}) के रूप देखें</span>
                                        <span aria-hidden='true'>·</span>
                                        <span lang='en'>See {longRoot.root}</span>
                                        <ArrowRight className='size-4' aria-hidden='true' />
                                    </button>
                                </Notice>
                            )}
                        </div>
                    </KalTabs>
                </Section>

                {notes && (
                    <Section
                        id='niyam'
                        hi='नियम'
                        en='The rules'
                        intro={{ hi: 'शिक्षक के नोट्स (प्राकृत-रचना-सौरभ) से।', en: 'From the teacher’s notes (प्राकृत-रचना-सौरभ).' }}
                    >
                        <div className={cn(PANEL, 'flex flex-col gap-4 p-5 sm:p-6')}>
                            <ol className='flex flex-col gap-3'>
                                {notes.rules.map((rule, i) => (
                                    <li key={rule.en} className='flex gap-3'>
                                        <span aria-hidden='true' className='grid size-7 shrink-0 place-items-center rounded-full bg-saffron/15 text-sm font-semibold text-saffron-strong'>
                                            {hiDigits(i + 1)}
                                        </span>
                                        <Bi hi={rule.hi} en={rule.en} className='gap-0.5 text-[0.9375rem] leading-relaxed' enClassName='text-sm' />
                                    </li>
                                ))}
                            </ol>
                            {notes.asides && notes.asides.length > 0 && (
                                <div className='adults-only flex flex-col gap-2 border-t border-border pt-4'>
                                    <p className='text-xs font-semibold text-muted-foreground'>
                                        <span lang='hi' className='hindi'>टिप्पणी</span>
                                        <span aria-hidden='true'> · </span>
                                        <span lang='en'>Notes (not asked in practice)</span>
                                    </p>
                                    <ul className='flex flex-col gap-2'>
                                        {notes.asides.map(aside => (
                                            <li key={aside.en}>
                                                <Bi hi={aside.hi} en={aside.en} className='gap-0.5 text-sm leading-relaxed text-muted-foreground' enClassName='text-xs' />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                <Section
                    id='dhatu-pratyaya'
                    hi='धातु + प्रत्यय'
                    en='Root + ending'
                    intro={tableMode === 'samanya'
                        ? { hi: 'सामान्य रूप, एक-एक करके बनते हुए। विकल्प के सूत्र देखने के लिए ऊपर "२. विकल्प" चुनें।', en: 'The standard forms, built one by one. Choose "२. विकल्प" above to see the formula of every variant.' }
                        : { hi: 'हर खाने का सूत्र और उसके सभी रूप, नोट्स के क्रम में।', en: 'The formula of every cell and all its forms, in the order of the notes.' }}
                >
                    <FormationCards root={root.root} table={table} mode={tableMode} />
                </Section>

                {examples.length > 0 && (
                    <Section id='udaharan' hi='उदाहरण' en='Examples'>
                        <ul className='grid gap-4 md:grid-cols-2'>
                            {examples.map(ex => (
                                <li key={ex.key} className={cn(PANEL, 'p-5')}>
                                    <TriLine pra={ex.pra} hi={ex.hi} en={ex.en} size='lg' />
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

                {(tense === 'vartaman' || tense === 'bhoot') && <AsNote tense={tense} />}

                <ul className='grid gap-4 md:grid-cols-2'>
                    <li>
                        <Link
                            href={`/grammar/kriya?root=${encodeURIComponent(root.root)}&tense=${tense}`}
                            className={cn(PANEL, 'group flex min-h-tap items-center gap-4 p-5 transition-colors hover:border-saffron/60')}
                        >
                            <span className='grid size-11 shrink-0 place-items-center rounded-2xl bg-saffron text-primary-foreground'>
                                <FlaskConical className='size-5' aria-hidden='true' />
                            </span>
                            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                <span lang='hi' className='hindi font-semibold'>धातुरूप प्रयोगशाला में अभ्यास करें</span>
                                <span lang='en' className='text-sm text-muted-foreground'>Build these forms step by step, compare tenses and use flashcards.</span>
                            </span>
                            <ArrowRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </li>
                    <li>
                        <Link
                            href={`/grammar/purush-vachan?root=${encodeURIComponent(root.root)}&tense=${tense}`}
                            className={cn(PANEL, 'group flex min-h-tap items-center gap-4 p-5 transition-colors hover:border-saffron/60')}
                        >
                            <span className='grid size-11 shrink-0 place-items-center rounded-2xl bg-secondary text-foreground'>
                                <Grid2x2 className='size-5' aria-hidden='true' />
                            </span>
                            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                <span lang='hi' className='hindi font-semibold'>पुरुष × वचन तालिका</span>
                                <span lang='en' className='text-sm text-muted-foreground'>Tap each cell for the pronoun, the sentence and its meaning.</span>
                            </span>
                            <ArrowRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </li>
                </ul>

                <Section id='abhyas' hi='अभ्यास' en='Quick check' intro={{ hi: 'सही प्रत्यय चुनिए; उत्तर तुरंत जाँचा जाता है।', en: 'Pick the right ending; your answer is checked straight away.' }}>
                    <PratyayaQuiz root={root.root} tense={tense} table={table} practiceHref={practiceLink(tense)} />
                </Section>

                <Section
                    id='sampurna-roop'
                    hi={`${label.hi}: ${root.root} धातु के सम्पूर्ण रूप`}
                    en={`Every form of ${root.root}`}
                    intro={{
                        hi: 'हर सर्वनाम के साथ इस धातु का रूप। शब्द पर टैप करके उसका व्याकरण देखें, सुनें बटन से आवाज़ सुनें।',
                        en: 'The form with every pronoun. Tap a word for its grammar; use listen to hear it.'
                    }}
                >
                    <KalTabs id='roop-tabs' value={formsMode} onChange={setFormsMode} label={`${label.hi}: ${root.root} के रूप · forms`}>
                        <FormsGrid table={table} mode={formsMode} root={root.root} tenseHi={label.hi} tenseEn={label.en} />
                    </KalTabs>
                </Section>

                <LabNav current='/grammar/kal' />
            </div>
        </div>
    )
}
